import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, Cell,
} from "recharts";
import {
  TrendUp, Users, Flame, ChartLine, CheckCircle, Sparkle, ArrowUpRight, Plus,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";

const STAGE_LABEL = {
  new: "New", contacted: "Contacted", proposal: "Proposal",
  negotiation: "Negotiation", won: "Won", lost: "Lost",
};

function fmtCurrency(n) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
}

function KpiCard({ label, value, sub, icon: Icon, accent, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      className="rounded-xl border border-border bg-card p-5 hover:-translate-y-0.5 transition-transform duration-200"
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="overline">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent || "bg-primary/15"}`}>
          <Icon size={16} weight="duotone" className="text-primary" />
        </div>
      </div>
      <div className="font-display text-3xl tracking-tight leading-none">{value}</div>
      <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
        <ArrowUpRight size={12} className="text-emerald-500" /> {sub}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [today, setToday] = useState({ tasks: [], followups: [] });
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);

  useEffect(() => {
    api.get("/dashboard/overview").then((r) => setData(r.data));
    api.get("/dashboard/activity").then((r) => setActivity(r.data));
    api.get("/dashboard/today").then((r) => setToday(r.data));
  }, []);

  const generateInsights = async () => {
    setInsightsLoading(true);
    try {
      const { data } = await api.post("/ai/insights");
      setInsights(data.insights || []);
    } catch (e) {
      setInsights(["Insight generation temporarily unavailable."]);
    } finally {
      setInsightsLoading(false);
    }
  };

  if (!data) {
    return <div className="text-muted-foreground text-sm">Loading dashboard…</div>;
  }

  const k = data.kpis;
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  })();

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="overline mb-2">— Executive overview</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight leading-none">
            {greeting}, <span className="gold-text">{user?.name?.split(" ")[0] || "Operator"}</span>.
          </h1>
          <p className="text-muted-foreground text-sm mt-3 max-w-xl leading-relaxed">
            Here's a precise look at your pipeline, revenue and what needs attention today.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/leads">
            <Button variant="outline" className="rounded-full" data-testid="quick-action-lead">
              <Plus size={14} className="mr-1.5" /> New lead
            </Button>
          </Link>
          <Link to="/customers">
            <Button className="rounded-full bg-primary text-primary-foreground" data-testid="quick-action-customer">
              <Plus size={14} className="mr-1.5" /> New customer
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Revenue (YTD)" value={fmtCurrency(k.revenue)} sub={`${k.leads_won} deals won`} icon={TrendUp} delay={0.05} />
        <KpiCard label="Active Clients" value={k.active_clients} sub={`${k.vip_clients} VIPs · ${k.total_customers} total`} icon={Users} delay={0.1} />
        <KpiCard label="Open Pipeline" value={k.leads_open} sub={`${k.win_rate}% win rate`} icon={Flame} delay={0.15} />
        <KpiCard label="Open Tasks" value={k.open_tasks} sub="Across all owners" icon={CheckCircle} delay={0.2} />
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-1">Revenue forecast</div>
              <h2 className="font-display text-xl tracking-tight">Six-month outlook</h2>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-primary"></span> Actual</span>
              <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Projected</span>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={data.forecast} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.5} />
                    <stop offset="95%" stopColor="#D4AF37" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "currentColor" }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fontSize: 11, fill: "currentColor" }} stroke="hsl(var(--border))" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v) => fmtCurrency(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#g1)" />
                <Area type="monotone" dataKey="projected" stroke="#10B981" strokeWidth={2} fill="url(#g2)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* AI Insights */}
        <div className="rounded-xl border border-border bg-card p-6 ai-glow relative overflow-hidden" data-testid="ai-insights">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-primary" />
              <div className="overline">AI Insights</div>
            </div>
            <Button
              size="sm" variant="ghost"
              onClick={generateInsights}
              disabled={insightsLoading}
              data-testid="generate-insights-btn"
              className="text-xs h-7"
            >
              {insightsLoading ? "Thinking…" : "Generate"}
            </Button>
          </div>
          <h3 className="font-display text-lg tracking-tight mb-4">Zippé AI Insights</h3>

          {insights.length === 0 ? (
            <p className="text-sm text-muted-foreground leading-relaxed">
              Generate an AI snapshot of where to focus this week — pipeline risks, hot deals, and next-best actions.
            </p>
          ) : (
            <ul className="space-y-3">
              {insights.map((i, idx) => (
                <li key={idx} className="text-sm leading-relaxed border-l-2 border-primary/50 pl-3">{i}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Pipeline + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="overline mb-1">Sales pipeline</div>
              <h2 className="font-display text-xl tracking-tight">By stage</h2>
            </div>
            <Link to="/leads" className="text-xs text-primary hover:underline">View Kanban →</Link>
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={data.pipeline.map(p => ({ ...p, label: STAGE_LABEL[p.stage] }))}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "currentColor" }} stroke="hsl(var(--border))" />
                <YAxis tick={{ fontSize: 11, fill: "currentColor" }} stroke="hsl(var(--border))" tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, n) => n === "value" ? fmtCurrency(v) : v}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {data.pipeline.map((p, i) => (
                    <Cell key={i} fill={p.stage === "won" ? "#10B981" : p.stage === "lost" ? "hsl(var(--muted-foreground))" : "#D4AF37"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <div className="overline mb-1">Team activity</div>
          <h2 className="font-display text-xl tracking-tight mb-5">Recent</h2>
          <ul className="space-y-4">
            {activity.slice(0, 6).map((a) => (
              <li key={a.id} className="flex gap-3 text-sm">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0"></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.actor || "system"}</div>
                </div>
              </li>
            ))}
            {activity.length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
          </ul>
        </div>
      </div>

      {/* Today */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="overline mb-1">Tasks for today</div>
          <h2 className="font-display text-xl tracking-tight mb-4">Focus list</h2>
          <ul className="divide-y divide-border/70">
            {today.tasks.slice(0, 6).map((t) => (
              <li key={t.id} className="py-2.5 flex items-center justify-between">
                <span className="text-sm truncate pr-3">{t.title}</span>
                <span className="overline">{t.priority}</span>
              </li>
            ))}
            {today.tasks.length === 0 && <li className="py-3 text-sm text-muted-foreground">Nothing pressing.</li>}
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="overline mb-1">Upcoming follow-ups</div>
          <h2 className="font-display text-xl tracking-tight mb-4">Stay in touch</h2>
          <ul className="divide-y divide-border/70">
            {today.followups.slice(0, 6).map((c) => (
              <li key={c.id} className="py-2.5 flex items-center justify-between">
                <span className="text-sm truncate pr-3">{c.company_name}</span>
                <span className="text-xs text-muted-foreground">{c.next_followup ? new Date(c.next_followup).toLocaleDateString() : "—"}</span>
              </li>
            ))}
            {today.followups.length === 0 && <li className="py-3 text-sm text-muted-foreground">All caught up.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
